import { useContext, useMemo } from "react";
import { useIntl } from "react-intl";
import { useMap } from "react-map-gl/maplibre";

import { FileIcon, LocationIcon, MenuAction, TenantContext, TrashIcon } from "@nens/ranalib";
import { combineUrlAndParams } from "@nens/shared-utils";

import { TAB_KEY } from "@/features/project";
import { ProjectContext } from "@/stores/project-provider";
import { convertFileIdToQueryParams } from "@/utils/convert-file-id-to-query-params";

import { getFileByPath, getIsEditable, getIsPublic } from "../../../store/selectors";
import { usePublicationStore } from "../../../store/store";
import { Layer } from "../../../types/publication";
import { useLayerBounds } from "../../map-layers/hooks/use-layer-bounds";

// FUTURE: Combine this with use-file-layer-item-actions.tsx
export function useStudyAreaActions(layer: Layer, removeStudyArea: () => void): MenuAction[] {
  const intl = useIntl();
  const map = useMap().current;
  const { bounds, boundsLoading } = useLayerBounds(layer);
  const { tenant } = useContext(TenantContext);
  const projectContext = useContext(ProjectContext);
  const isPublic = usePublicationStore(getIsPublic);
  const isEditable = usePublicationStore(getIsEditable);
  const file = usePublicationStore(getFileByPath(layer.filePath));

  return useMemo((): MenuAction[] => {
    const actions: MenuAction[] = [
      {
        label: intl.formatMessage({
          id: "publication.study_area.contextmenu.zoom_to_study_area",
          defaultMessage: "Zoom to study area",
        }),
        icon: <LocationIcon />,
        action: () => {
          if (map && bounds) {
            map.fitBounds([
              [bounds[0], bounds[1]],
              [bounds[2], bounds[3]],
            ]);
          }
        },
        disabledText:
          !boundsLoading && bounds
            ? undefined
            : intl.formatMessage({
                id: "publication.study_area.contextmenu.zoom_to_study_area.no_bounds",
                defaultMessage: "Zoom to study area (bounds not available)",
              }),
      },
    ];

    if (file) {
      const url = `${combineUrlAndParams(
        `${window.location.origin}/${tenant}/projects/${projectContext.slug}`,
        { [TAB_KEY]: 1 }
      )}&${convertFileIdToQueryParams(file.file.id, file.ref)}`;

      if (!isPublic) {
        actions.push({
          icon: <FileIcon />,
          label: intl.formatMessage({
            id: "publication.study_area.contextmenu.view_file",
            defaultMessage: "View file",
          }),
          to: url,
        });
      }
    }

    if (!isPublic && isEditable) {
      actions.push({
        label: intl.formatMessage({
          id: "publication.study_area.contextmenu.remove_study_area",
          defaultMessage: "Remove study area",
        }),
        icon: <TrashIcon />,
        action: removeStudyArea,
      });
    }

    return actions;
  }, [
    bounds,
    boundsLoading,
    file,
    intl,
    isEditable,
    isPublic,
    map,
    projectContext.slug,
    removeStudyArea,
    tenant,
  ]);
}
